import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, Plus, Lock, KeyRound, X, Globe, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import apiService from '../services/api';

const UninstallForm = ({ onJobCreated, savedConfig }) => {
  const [nodes, setNodes] = useState([
    { host: '', privateIp: '', username: '', password: '', privateKey: '', authType: 'password' }
  ]);
  const [installPath, setInstallPath] = useState('/opt/kafka');
  const [dataPath, setDataPath] = useState('/var/lib/kafka');
  const [logPath, setLogPath] = useState('/var/log/kafka');
  const [useKRaft, setUseKRaft] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const [useSharedCredentials, setUseSharedCredentials] = useState(true);
  const [sharedCredentials, setSharedCredentials] = useState({
    username: '',
    password: '',
    privateKey: '',
    authType: 'password'
  });

  useEffect(() => {
    if (savedConfig) {
      if (savedConfig.nodes) {
        setNodes(savedConfig.nodes.map(n => ({
          host: n.host || '',
          privateIp: n.privateIp || '',
          username: '',
          password: '',
          privateKey: '',
          authType: 'password'
        })));
      }
      if (savedConfig.installPath) setInstallPath(savedConfig.installPath);
      if (savedConfig.dataPath) setDataPath(savedConfig.dataPath);
      if (savedConfig.logPath) setLogPath(savedConfig.logPath);
      if (savedConfig.useKRaft !== undefined) setUseKRaft(savedConfig.useKRaft);
    }
  }, [savedConfig]);

  const addNode = () => {
    setNodes([...nodes, { host: '', privateIp: '', username: '', password: '', privateKey: '', authType: 'password' }]);
  };

  const removeNode = (index) => {
    if (nodes.length > 1) {
      setNodes(nodes.filter((_, i) => i !== index));
    }
  };

  const updateNode = (index, field, value) => {
    const updatedNodes = [...nodes];
    updatedNodes[index][field] = value;
    setNodes(updatedNodes);
  };

  const applySharedCredentialsToAll = () => {
    const updatedNodes = nodes.map(node => ({
      ...node,
      username: sharedCredentials.username,
      password: sharedCredentials.authType === 'password' ? sharedCredentials.password : '',
      privateKey: sharedCredentials.authType === 'key' ? sharedCredentials.privateKey : '',
      authType: sharedCredentials.authType
    }));
    setNodes(updatedNodes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!confirmed) {
      alert('Please confirm that you want to uninstall Kafka');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const finalNodes = nodes.map(node => {
        const creds = useSharedCredentials ? sharedCredentials : node;
        return {
          host: node.host,
          privateIp: node.privateIp || '',
          username: creds.username,
          password: creds.authType === 'password' ? creds.password : undefined,
          privateKey: creds.authType === 'key' ? creds.privateKey : undefined
        };
      });

      for (const node of finalNodes) {
        if (!node.host || !node.username) {
          throw new Error('All nodes must have host and username');
        }
        if (!node.password && !node.privateKey) {
          throw new Error('All nodes must have either password or private key');
        }
      }

      const jobData = {
        nodes: finalNodes,
        installPath,
        dataPath,
        logPath,
        useKRaft
      };

      const response = await apiService.createUninstallJob(jobData);

      if (response.success) {
        onJobCreated(response.jobId);
        setConfirmed(false);
      } else {
        throw new Error(response.message || 'Failed to create uninstall job');
      }

    } catch (err) {
      setError(err.message || 'Failed to submit uninstall request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2">
        <Trash2 className="h-5 w-5 text-destructive" />
        <h2 className="text-base font-bold text-destructive">Uninstall Kafka Cluster</h2>
      </div>

      <Alert variant="destructive" className="py-3 border-destructive/50 bg-destructive/10">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle className="text-sm">Warning: This action cannot be undone!</AlertTitle>
        <AlertDescription className="text-xs">
          All Kafka data, configurations, and services will be permanently removed.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      )}

      <Card className="p-4">
        <span className="text-sm font-semibold">Installation Paths</span>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Install Path</Label>
            <Input value={installPath} onChange={(e) => setInstallPath(e.target.value)} className="h-8 text-xs" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Data Path</Label>
            <Input value={dataPath} onChange={(e) => setDataPath(e.target.value)} className="h-8 text-xs" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Log Path</Label>
            <Input value={logPath} onChange={(e) => setLogPath(e.target.value)} className="h-8 text-xs" />
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-destructive" />
            <span className="text-sm font-semibold">SSH Credentials</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="useSharedUninstall" checked={useSharedCredentials} onCheckedChange={setUseSharedCredentials} />
            <Label htmlFor="useSharedUninstall" className="text-xs cursor-pointer">Use same credentials for all servers</Label>
          </div>
        </div>

        {useSharedCredentials && (
          <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Username</Label>
                <Input
                  value={sharedCredentials.username}
                  onChange={(e) => setSharedCredentials({ ...sharedCredentials, username: e.target.value })}
                  placeholder="ec2-user"
                  className="h-8 text-xs"
                />
              </div>
              <div className="col-span-2">
                <Tabs value={sharedCredentials.authType} onValueChange={(v) => setSharedCredentials({ ...sharedCredentials, authType: v })}>
                  <TabsList className="h-7 w-full grid grid-cols-2 bg-muted/50 mb-2">
                    <TabsTrigger value="password" className="text-xs h-6"><Lock className="h-3 w-3 mr-1" />Password</TabsTrigger>
                    <TabsTrigger value="key" className="text-xs h-6"><KeyRound className="h-3 w-3 mr-1" />SSH Key</TabsTrigger>
                  </TabsList>
                  <TabsContent value="password" className="mt-0">
                    <Input type="password" value={sharedCredentials.password} onChange={(e) => setSharedCredentials({ ...sharedCredentials, password: e.target.value })} placeholder="SSH Password" className="h-8 text-xs" />
                  </TabsContent>
                  <TabsContent value="key" className="mt-0">
                    <Textarea value={sharedCredentials.privateKey} onChange={(e) => setSharedCredentials({ ...sharedCredentials, privateKey: e.target.value })} placeholder="-----BEGIN RSA PRIVATE KEY-----" className="h-20 text-xs font-mono resize-none" />
                  </TabsContent>
                </Tabs>
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" size="sm" onClick={applySharedCredentialsToAll} className="h-8 text-xs w-full">
                  <Copy className="h-3 w-3 mr-1" />Apply to All
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Target Servers</span>
            <Badge variant="destructive" className="text-xs h-5 px-1.5">{nodes.length}</Badge>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addNode} className="h-8 text-xs">
            <Plus className="h-4 w-4 mr-1" />Add Server
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-3 max-h-[250px] overflow-y-auto pr-1">
          {nodes.map((node, index) => (
            <div key={index} className="p-3 border border-destructive/30 rounded-lg space-y-2 bg-destructive/5">
              <div className="flex items-center justify-between">
                <Badge variant="destructive" className="text-xs h-5">Node {index + 1}</Badge>
                {nodes.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeNode(index)} className="h-6 w-6 p-0">
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground flex items-center gap-1"><Globe className="h-3 w-3" />Public IP / Host</Label>
                <Input value={node.host} onChange={(e) => updateNode(index, 'host', e.target.value)} placeholder="54.xx.xx.xx" className="h-8 text-xs" required />
              </div>
              {!useSharedCredentials && (
                <>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Username</Label>
                    <Input value={node.username} onChange={(e) => updateNode(index, 'username', e.target.value)} placeholder="ec2-user" className="h-8 text-xs" required />
                  </div>
                  <Tabs value={node.authType} onValueChange={(v) => updateNode(index, 'authType', v)}>
                    <TabsList className="h-6 w-full grid grid-cols-2 bg-muted/50">
                      <TabsTrigger value="password" className="text-[10px] h-5"><Lock className="h-2.5 w-2.5 mr-0.5" />Password</TabsTrigger>
                      <TabsTrigger value="key" className="text-[10px] h-5"><KeyRound className="h-2.5 w-2.5 mr-0.5" />SSH Key</TabsTrigger>
                    </TabsList>
                    <TabsContent value="password" className="mt-1">
                      <Input type="password" value={node.password} onChange={(e) => updateNode(index, 'password', e.target.value)} placeholder="Password" className="h-7 text-xs" />
                    </TabsContent>
                    <TabsContent value="key" className="mt-1">
                      <Textarea value={node.privateKey} onChange={(e) => updateNode(index, 'privateKey', e.target.value)} placeholder="SSH Key" className="h-14 text-[10px] font-mono resize-none" />
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3 p-3 rounded-lg border border-destructive/50 bg-destructive/10">
        <Checkbox id="confirm" checked={confirmed} onCheckedChange={setConfirmed} className="h-5 w-5 border-destructive data-[state=checked]:bg-destructive" />
        <label htmlFor="confirm" className="text-sm cursor-pointer">I understand this will <strong>permanently delete</strong> all Kafka data</label>
      </div>

      <Button type="submit" variant="destructive" className="w-full h-10 text-sm font-semibold" disabled={loading || !confirmed}>
        {loading ? (
          <><span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />Creating Uninstall Job...</>
        ) : (
          <><Trash2 className="h-4 w-4 mr-2" />Uninstall Kafka Cluster</>
        )}
      </Button>
    </form>
  );
};

export default UninstallForm;
